import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { DwMessage, DwMessageSeverity } from '@devwareapps/devware-cap';
import { FitnessCommerceService } from '../../services/fitness-commerce.service';
import { CheckoutConfiguration, CommerceProduct } from '../../models/commerce.models';
import { FitnessReferenceDataService, RegistrationOptions } from '../../../shared/services/fitness-reference-data.service';
import { ChallengeDefinitionEntity } from '../../../../meta-data/app-meta-data.service';


@Component({ selector: 'app-fitness-checkout-page', template: `
    <app-season-banner *ngIf="commerce.cart?.state !== 'Paid'" label="Checkout"
      heading="Just a few clicks away from helping families" [challenge]="challenge"></app-season-banner>
    <main class="checkout-page"><a routerLink="/challenge-registration" [queryParams]="{intent: 'store'}">Back to store</a>
      <p *ngIf="loading" role="status">Loading your order…</p>
      <dw-message *ngIf="pageMessage as notice" [message]="notice"></dw-message><button *ngIf="error" type="button" (click)="load()">Retry loading</button>
      <p *ngIf="!loading && !error && !commerce.cart"><a routerLink="/challenge-registration">Return to registration to build your order →</a></p>
      <app-fitness-checkout *ngIf="configuration && !error && commerce.cart" [products]="products" [configuration]="configuration" [registrationOptions]="registrationOptions"></app-fitness-checkout>
    </main>`, styles: [`.checkout-page {max-width: 1100px; margin: auto; padding: 32px 24px;} h1 {color: var(--dw-navbar-bg);}`] })
export class FitnessCheckoutPageComponent implements OnInit, OnDestroy {
    challenge: ChallengeDefinitionEntity;
    products: CommerceProduct[] = [];
    configuration: CheckoutConfiguration;
    registrationOptions: RegistrationOptions = { years: [], sources: [] };
    loading = false;
    error = '';
    get pageMessage(): DwMessage {
        const body = this.error || (!this.loading && !this.commerce.cart ? 'Your order is empty. Add an item in the store.' : '');
        return body ? { messageBody: body, allowClose: false,
            severity: this.error ? DwMessageSeverity.error : DwMessageSeverity.info } : null;
    }
    private sub = new Subscription();
    private loadSub: Subscription;
    constructor(public commerce: FitnessCommerceService, private route: ActivatedRoute, private reference: FitnessReferenceDataService) {}
    ngOnInit(): void {
        this.sub.add(this.reference.getActiveChallenge().pipe(take(1)).subscribe(challenge => this.challenge = challenge,
            () => { this.challenge = null; }));
        this.sub.add(this.commerce.accountChanged$.subscribe(() => {
            this.loadSub?.unsubscribe(); this.loading = false;
            this.configuration = null; this.products = [];
            this.registrationOptions = { years: [], sources: [] };
            this.error = 'Your account changed. Reload checkout to load your order and address.';
        }));
        this.load();
    }
    load(): void {
        if (this.loading) { return; }
        this.loading = true; this.error = '';
        const id = +this.route.snapshot.queryParamMap.get('order');
        const cart = this.commerce.loadForCheckout(id > 0 ? id : undefined).pipe(take(1));
        const options = cart.pipe(switchMap(order => order?.cart.lines.some(l => l.input.kind === 'registration')
            ? this.reference.getRegistrationOptions() : of({ years: [], sources: [] })), take(1));
        this.loadSub = combineLatest([this.commerce.products(), this.commerce.configuration(), options]).pipe(take(1)).subscribe(([products, config, options]) => {
                this.products = products; this.configuration = config; this.registrationOptions = options; this.loading = false;
            }, error => { this.loading = false; this.error = error.error && (error.error.detail || error.error.title) || 'Unable to load checkout.'; });
        this.sub.add(this.loadSub);
    }
    ngOnDestroy(): void { this.sub.unsubscribe(); }
}

